import React, { useState, useEffect, useMemo, useCallback } from 'react';
import DocumentContext from './DocumentContext';
import { useWorkspace } from '../../workspace/state/useWorkspace';
import {
  getDocuments as apiGetDocuments,
  getDocument as apiGetDocument,
  createDocument as apiCreateDocument,
  updateDocument as apiUpdateDocument,
  deleteDocument as apiDeleteDocument,
  duplicateDocument as apiDuplicateDocument,
} from '../api/documentApi';

/**
 * DocumentProvider coordinates all document states (Master Resume, Variants, Cover Letters)
 * for the currently active workspace.
 */
export const DocumentProvider = ({ children }) => {
  const { activeWorkspace } = useWorkspace();
  
  const [activeDocument, setActiveDocument] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedCoverLetter, setSelectedCoverLetter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Helper to determine document type by ID from the current list.
   */
  const getDocType = useCallback((id) => {
    const doc = documents.find((d) => d.id === id);
    return doc?.type || 'resume';
  }, [documents]);

  /**
   * Loads all documents inside the workspace.
   */
  const loadDocuments = useCallback(async (workspaceId) => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiGetDocuments(workspaceId);
      setDocuments(data || []);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to load documents');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Loads the full payload of a single document and updates active states.
   */
  const loadDocument = useCallback(async (documentId) => {
    setLoading(true);
    setError(null);
    try {
      const type = getDocType(documentId);
      const data = await apiGetDocument(documentId, type);
      setActiveDocument(data);
      
      if (type === 'variant') {
        setSelectedVariant(data);
        setSelectedCoverLetter(null);
      } else if (type === 'cover_letter') {
        setSelectedCoverLetter(data);
        setSelectedVariant(null);
      } else {
        setSelectedVariant(null);
        setSelectedCoverLetter(null);
      }
      return data;
    } catch (err) {
      setError(err.message || 'Failed to load document');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getDocType]);

  /**
   * Creates a new document within the active workspace and updates the list.
   */
  const createDocument = useCallback(async (type, name, extra = {}) => {
    if (!activeWorkspace?.id) {
      throw new Error('No active workspace selected');
    }
    setLoading(true);
    setError(null);
    try {
      const defaultName = name || `New ${type.charAt(0).toUpperCase() + type.slice(1)}`;
      const result = await apiCreateDocument(activeWorkspace.id, type, defaultName, extra);
      await loadDocuments(activeWorkspace.id);
      return result;
    } catch (err) {
      setError(err.message || 'Failed to create document');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [activeWorkspace?.id, loadDocuments]);

  /**
   * Updates an existing document properties (e.g. metadata).
   */
  const updateDocument = useCallback(async (id, data) => {
    setLoading(true);
    setError(null);
    try {
      const type = getDocType(id);
      const result = await apiUpdateDocument(id, type, data);
      
      setDocuments((prev) =>
        prev.map((d) => (d.id === id ? { ...d, ...result } : d))
      );
      setActiveDocument((prev) =>
        prev && (prev.document?.id === id || prev.id === id) ? { ...prev, ...result } : prev
      );
      return result;
    } catch (err) {
      setError(err.message || 'Failed to update document');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getDocType]);

  /**
   * Deletes a document and updates active selections.
   */
  const deleteDocument = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      await apiDeleteDocument(id);
      setDocuments((prev) => prev.filter((d) => d.id !== id));
      
      setActiveDocument((prev) => {
        const prevId = prev?.document?.id || prev?.id;
        return prevId === id ? null : prev;
      });
      
      if (selectedVariant?.id === id) setSelectedVariant(null);
      if (selectedCoverLetter?.id === id) setSelectedCoverLetter(null);
    } catch (err) {
      setError(err.message || 'Failed to delete document');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [selectedVariant, selectedCoverLetter]);

  /**
   * Duplicates a document and reloads the document list.
   */
  const duplicateDocument = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const type = getDocType(id);
      const result = await apiDuplicateDocument(id, type);
      if (activeWorkspace?.id) {
        await loadDocuments(activeWorkspace.id);
      }
      return result;
    } catch (err) {
      setError(err.message || 'Failed to duplicate document');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [activeWorkspace?.id, getDocType, loadDocuments]);

  /**
   * Updates active state, syncing sub-states automatically.
   */
  const changeActiveDocument = useCallback((doc) => {
    setActiveDocument(doc);
    if (doc) {
      const type = doc.type || doc.document?.type;
      if (type === 'variant') {
        setSelectedVariant(doc);
        setSelectedCoverLetter(null);
      } else if (type === 'cover_letter') {
        setSelectedCoverLetter(doc);
        setSelectedVariant(null);
      } else {
        setSelectedVariant(null);
        setSelectedCoverLetter(null);
      }
    } else {
      setSelectedVariant(null);
      setSelectedCoverLetter(null);
    }
  }, []);

  // Automatically clear active document and reload document list when workspace changes
  useEffect(() => {
    setActiveDocument(null);
    setSelectedVariant(null);
    setSelectedCoverLetter(null);

    if (activeWorkspace?.id) {
      loadDocuments(activeWorkspace.id).catch(() => {});
    } else {
      setDocuments([]);
    }
  }, [activeWorkspace?.id, loadDocuments]);

  const value = useMemo(() => ({
    activeDocument,
    documents,
    selectedVariant,
    selectedCoverLetter,
    loading,
    error,
    loadDocuments,
    loadDocument,
    createDocument,
    updateDocument,
    deleteDocument,
    duplicateDocument,
    setActiveDocument: changeActiveDocument,
    setDocuments,
    setSelectedVariant,
    setSelectedCoverLetter,
    setLoading,
    setError
  }), [
    activeDocument,
    documents,
    selectedVariant,
    selectedCoverLetter,
    loading,
    error,
    loadDocuments,
    loadDocument,
    createDocument,
    updateDocument,
    deleteDocument,
    duplicateDocument,
    changeActiveDocument
  ]);

  return (
    <DocumentContext.Provider value={value}>
      {children}
    </DocumentContext.Provider>
  );
};

export default DocumentProvider;
